import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress, Snackbar, Alert } from '@mui/material';
import styled, { css } from 'styled-components';
import { Check } from 'lucide-react';
import DashboardNavbar from '../dashboardNavbar';
import { SYSTEM_TABS } from '../admin/adminSectionTabs';
import DataTable from '../ui/DataTable';
import { Chip, Cbx, RowBetween, Muted, Input, Select, Tag } from '../ui/primitives';
import { isSuperAdmin } from '../services/instructorPortalAccess';
import { authHeaders, parseError } from '../services/campaignAdminApi';
import CourseAudienceDialog from './CourseAudienceDialog';
import {
  AUDIENCE_MODE,
  PUBLISHING_LIST_DEFAULTS,
  PUBLISHING_SORT_FIELDS,
  applyAudienceLocally,
  audienceSummary,
  getNextPublishingSort,
  sortPublishingRowsLocally,
  filterPublishingRows,
  applyVisibilityLocally,
} from './publishingListApi';

const PAGE_SIZE = 20;

const apiUrl = process.env.REACT_APP_API_URL;

/* ---- fetch conventions: reuses campaignAdminApi.js's Basic-auth helpers instead of
   re-declaring them (matches CampaignManager.js's underlying service conventions). ---- */

async function fetchPublishingRows(adminUserId) {
  const response = await fetch(
    `${apiUrl}/api/admin/course-publishing?adminUserId=${encodeURIComponent(adminUserId)}`,
    { method: 'GET', headers: authHeaders() },
  );
  if (!response.ok) {
    throw await parseError(response, `Publishing list failed: ${response.status}`);
  }
  return response.json();
}

async function updateCoursePublishing(courseId, payload) {
  const response = await fetch(
    `${apiUrl}/api/admin/course-publishing/${encodeURIComponent(courseId)}`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload) },
  );
  if (!response.ok) {
    throw await parseError(response, `Update failed: ${response.status}`);
  }
  return response;
}

async function fetchOrganizations(adminUserId) {
  const response = await fetch(
    `${apiUrl}/api/admin/course-publishing/organizations?adminUserId=${encodeURIComponent(adminUserId)}`,
    { method: 'GET', headers: authHeaders() },
  );
  if (!response.ok) {
    throw await parseError(response, `Organization list failed: ${response.status}`);
  }
  return response.json();
}

async function updateCourseAudience(courseId, payload) {
  const response = await fetch(
    `${apiUrl}/api/admin/course-publishing/${encodeURIComponent(courseId)}/audience`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload) },
  );
  if (!response.ok) {
    throw await parseError(response, `Audience update failed: ${response.status}`);
  }
  return response;
}

async function bulkUpdateCoursePublishing(payload) {
  const response = await fetch(
    `${apiUrl}/api/admin/course-publishing/bulk`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload) },
  );
  if (!response.ok) {
    throw await parseError(response, `Bulk update failed: ${response.status}`);
  }
  return response;
}

const TYPE_LABEL = (t) => t || '—';

/**
 * Amber flags the one audience state that silently reaches nobody: restricted to a
 * selected list that is currently empty. Publishing to everyone stays neutral because
 * it is the default, not a decision.
 */
function audienceTone(row) {
  if (Number(row.catalogAudienceMode) !== AUDIENCE_MODE.selectedOrgs) return 'gray';
  return (row.audienceOrgIds || []).length === 0 ? 'amber' : 'accent';
}

const feedbackKey = (courseId, field) => `${courseId}::${field}`;

/**
 * Captures each targeted course's current value of `field`, keyed by courseId.
 * MUST be called against the live rows array (e.g. inside a functional `setRows`
 * updater), never against a `selectedRows` snapshot — the selection can go stale
 * if a row is toggled individually after being selected but before a bulk action
 * runs on it, and rolling back to the stale value would silently misrepresent
 * what's actually visible to learners.
 */
export function capturePreviousFieldValues(rows, courseIds, field) {
  const idSet = new Set(courseIds || []);
  const map = new Map();
  (rows || []).forEach((r) => {
    if (idSet.has(r.courseId)) {
      map.set(r.courseId, r[field] === 1 ? 1 : 0);
    }
  });
  return map;
}

/** Super-admin console for per-course catalog/store visibility. */
export default function PublishingManager() {
  const userType = localStorage.getItem('userType');
  const allowed = isSuperAdmin(userType);
  const adminUserId = localStorage.getItem('userId');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const [sortBy, setSortBy] = useState(PUBLISHING_LIST_DEFAULTS.sortBy);
  const [sortDir, setSortDir] = useState(PUBLISHING_LIST_DEFAULTS.sortDir);
  const [query, setQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');

  // Audience editing. Organizations are fetched on first open rather than with the
  // page, since most visits never touch the audience column.
  const [audienceCourse, setAudienceCourse] = useState(null);
  const [audienceSaving, setAudienceSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState('');
  const orgsLoadedRef = useRef(false);

  const [savingKeys, setSavingKeys] = useState(() => new Set());
  const [fieldFeedback, setFieldFeedback] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableResetKey, setTableResetKey] = useState(0);
  // Rollback map for the in-flight bulk action, captured fresh from live row state
  // (see capturePreviousFieldValues) — never read from the selectedRows snapshot.
  const bulkPreviousRef = useRef(new Map());

  const showToast = (msg, severity = 'success') => setToast({ open: true, msg, severity });

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchPublishingRows(adminUserId);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load the publishing list.');
    } finally {
      setLoading(false);
    }
  }, [allowed, adminUserId]);

  useEffect(() => { load(); }, [load]);

  const ownerOptions = useMemo(() => {
    const seen = new Map();
    (rows || []).forEach((r) => {
      if (r.ownerUserId != null && !seen.has(r.ownerUserId)) {
        seen.set(r.ownerUserId, r.ownerName || r.ownerEmail || String(r.ownerUserId));
      }
    });
    return Array.from(seen.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filteredRows = useMemo(
    () => filterPublishingRows(rows, { query, ownerUserId: ownerFilter || null }),
    [rows, query, ownerFilter],
  );
  const sortedRows = useMemo(
    () => sortPublishingRowsLocally(filteredRows, sortBy, sortDir),
    [filteredRows, sortBy, sortDir],
  );

  if (!allowed) {
    return (
      <div>
        <DashboardNavbar menuItems={SYSTEM_TABS} />
        <Shell>
          <Inner>
            <AccessDenied>
              <h2>Access restricted</h2>
              <p>Course publishing controls are only available to StudyAK super admins.</p>
            </AccessDenied>
          </Inner>
        </Shell>
      </div>
    );
  }

  const setSaving = (courseId, field, on) => {
    const key = feedbackKey(courseId, field);
    setSavingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  const setFeedback = (courseId, field, status) => {
    const key = feedbackKey(courseId, field);
    setFieldFeedback((prev) => ({ ...prev, [key]: status }));
    if (status === 'saved') {
      setTimeout(() => {
        setFieldFeedback((prev) => {
          if (prev[key] !== 'saved') return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }, 2200);
    }
  };

  const handleSortChange = (columnKey) => {
    const next = getNextPublishingSort(columnKey, sortBy, sortDir);
    setSortBy(next.sortBy);
    setSortDir(next.sortDir);
  };

  // Toggling a single checkbox: optimistic update, single-flag PUT, revert + error banner on failure.
  const handleToggle = async (row, field) => {
    const key = feedbackKey(row.courseId, field);
    if (savingKeys.has(key)) return;
    const previous = row[field] === 1 ? 1 : 0;
    const next = previous === 1 ? 0 : 1;

    setRows((list) => applyVisibilityLocally(list, [row.courseId], { [field]: next }));
    setSaving(row.courseId, field, true);
    setFieldFeedback((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });

    try {
      await updateCoursePublishing(row.courseId, { adminUserId, [field]: next });
      setFeedback(row.courseId, field, 'saved');
    } catch (e) {
      setRows((list) => applyVisibilityLocally(list, [row.courseId], { [field]: previous }));
      setFeedback(row.courseId, field, 'error');
      showToast(
        `${row.courseName || 'Course'} — change was NOT saved: ${e.message || 'update failed'}`,
        'error',
      );
    } finally {
      setSaving(row.courseId, field, false);
    }
  };

  const openAudienceDialog = async (row) => {
    setAudienceCourse(row);
    if (orgsLoadedRef.current) return;

    setOrgsLoading(true);
    setOrgsError('');
    try {
      const data = await fetchOrganizations(adminUserId);
      setOrganizations(Array.isArray(data) ? data : []);
      orgsLoadedRef.current = true;
    } catch (e) {
      setOrgsError(e.message || 'Failed to load organizations.');
    } finally {
      setOrgsLoading(false);
    }
  };

  // Audience is saved before the local row changes: unlike the visibility checkboxes
  // there is no single value to roll back to, and showing a stale audience would
  // misrepresent which instructors can reach the course.
  const handleAudienceSave = async ({ mode, orgIds }) => {
    if (!audienceCourse || audienceSaving) return;
    const { courseId, courseName } = audienceCourse;

    setAudienceSaving(true);
    try {
      await updateCourseAudience(courseId, { adminUserId, mode, orgIds });
      setRows((list) => applyAudienceLocally(list, courseId, { mode, orgIds }));
      setAudienceCourse(null);
      showToast(
        mode === AUDIENCE_MODE.allOrgs
          ? `${courseName || 'Course'} is now in every organization's catalog.`
          : `${courseName || 'Course'} is now limited to ${orgIds.length} organization${orgIds.length === 1 ? '' : 's'}.`,
      );
    } catch (e) {
      showToast(
        `${courseName || 'Course'} — audience was NOT saved: ${e.message || 'update failed'}`,
        'error',
      );
    } finally {
      setAudienceSaving(false);
    }
  };

  const clearSelection = () => {
    setTableResetKey((k) => k + 1);
    setSelectedRows([]);
  };

  // Bulk apply: optimistic update across all selected rows, PUT /bulk, per-row revert + error banner on failure.
  const handleBulkApply = async (field, value, label) => {
    if (selectedRows.length === 0 || bulkSaving) return;
    const courseIds = selectedRows.map((r) => r.courseId);

    setBulkSaving(true);
    // Capture the rollback map from LIVE row state inside the functional updater —
    // not from the `selectedRows` snapshot, which can be stale if one of the
    // selected rows was toggled individually after being selected but before this
    // bulk action ran. Reverting to a stale value would silently misrepresent what
    // learners can currently see.
    setRows((list) => {
      bulkPreviousRef.current = capturePreviousFieldValues(list, courseIds, field);
      return applyVisibilityLocally(list, courseIds, { [field]: value });
    });

    try {
      await bulkUpdateCoursePublishing({ adminUserId, courseIds, [field]: value });
      showToast(`${courseIds.length} course${courseIds.length === 1 ? '' : 's'} set to ${label}.`);
      clearSelection();
    } catch (e) {
      const previousByCourseId = bulkPreviousRef.current;
      setRows((list) => (list || []).map((r) => (
        previousByCourseId.has(r.courseId)
          ? { ...r, [field]: previousByCourseId.get(r.courseId) }
          : r
      )));
      showToast(
        `Bulk update FAILED — no changes were saved: ${e.message || 'update failed'}`,
        'error',
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const renderCheckbox = (row, field) => {
    const on = row[field] === 1;
    const key = feedbackKey(row.courseId, field);
    const saving = savingKeys.has(key);
    const feedback = fieldFeedback[key];
    return (
      <CheckboxCell onClick={(e) => e.stopPropagation()}>
        <Cbx
          $on={on}
          onClick={() => handleToggle(row, field)}
          style={{ opacity: saving ? 0.5 : 1, cursor: saving ? 'wait' : 'pointer' }}
          title={on ? 'Visible — click to hide' : 'Hidden — click to show'}
        >
          <Check size={13} />
        </Cbx>
        {saving && <CircularProgress size={12} />}
        {!saving && feedback === 'saved' && <FieldStatus>Saved</FieldStatus>}
        {!saving && feedback === 'error' && <FieldStatus $tone="error">Failed</FieldStatus>}
      </CheckboxCell>
    );
  };

  const bulkActions = (
    <>
      {bulkSaving && <CircularProgress size={18} />}
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply('catalogVisible', 1, 'visible in catalog')}
      >
        Show in catalog
      </Chip>
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply('catalogVisible', 0, 'hidden from catalog')}
      >
        Hide from catalog
      </Chip>
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply('storeVisible', 1, 'visible in store')}
      >
        Show in store
      </Chip>
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply('storeVisible', 0, 'hidden from store')}
      >
        Hide from store
      </Chip>
      <Chip $soft style={{ cursor: 'pointer' }} onClick={clearSelection}>
        Clear selection
      </Chip>
    </>
  );

  return (
    <div>
      <DashboardNavbar menuItems={SYSTEM_TABS} />
      <Shell>
        <Inner>
          <PageIntro>
            <RowBetween>
              <div>
                <h2>Publishing</h2>
                <p>
                  Control whether each course is visible in the instructor catalog and in the
                  learner store, and which organizations can see it in the catalog. Changes take
                  effect immediately.
                </p>
              </div>
            </RowBetween>
            {!loading && !error && (
              <StatsRow>
                <span><b>{rows.length}</b> courses</span>
                <span><b>{rows.filter((r) => r.catalogVisible === 1).length}</b> in catalog</span>
                <span>
                  <b>{rows.filter((r) => Number(r.catalogAudienceMode) === AUDIENCE_MODE.selectedOrgs).length}</b>
                  {' '}org-restricted
                </span>
                <span><b>{rows.filter((r) => r.storeVisible === 1).length}</b> in store</span>
              </StatsRow>
            )}
          </PageIntro>

          {loading && (
            <LoadingWrap><CircularProgress size={28} /></LoadingWrap>
          )}

          {!loading && error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>
          )}

          {!loading && !error && (
            <>
              <ToolbarRow>
                <SearchWrap>
                  <Input
                    type="search"
                    placeholder="Search course name…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </SearchWrap>
                <OwnerWrap>
                  <label htmlFor="publishing-owner-filter">Owner</label>
                  <Select
                    id="publishing-owner-filter"
                    value={ownerFilter}
                    onChange={(e) => setOwnerFilter(e.target.value)}
                  >
                    <option value="">All owners</option>
                    {ownerOptions.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </Select>
                </OwnerWrap>
              </ToolbarRow>

              <DataTable
                key={tableResetKey}
                rows={sortedRows}
                rowKey={(r) => r.courseId}
                pageSize={PAGE_SIZE}
                selectable
                onSelectionChange={setSelectedRows}
                bulkActions={bulkActions}
                sortBy={sortBy}
                sortDir={sortDir}
                onSortChange={handleSortChange}
                primary={{
                  header: 'Course',
                  label: (r) => r.courseName || '(untitled)',
                  sub: (r) => r.courseId,
                  short: (r) => (r.courseName || '?').charAt(0).toUpperCase(),
                  sortKey: PUBLISHING_SORT_FIELDS.courseName,
                }}
                columns={[
                  {
                    header: 'Owner',
                    sortKey: PUBLISHING_SORT_FIELDS.ownerName,
                    render: (r) => r.ownerName || r.ownerEmail || <Muted>—</Muted>,
                  },
                  {
                    header: 'Type',
                    sortKey: PUBLISHING_SORT_FIELDS.courseType,
                    width: 120,
                    muted: true,
                    render: (r) => TYPE_LABEL(r.courseType),
                  },
                  {
                    header: 'Catalog',
                    width: 130,
                    render: (r) => renderCheckbox(r, 'catalogVisible'),
                  },
                  {
                    header: 'Audience',
                    sortKey: PUBLISHING_SORT_FIELDS.audience,
                    width: 170,
                    render: (r) => {
                      const inCatalog = r.catalogVisible === 1;
                      return (
                        <AudienceCell onClick={(e) => e.stopPropagation()}>
                          <Tag
                            $tone={audienceTone(r)}
                            style={{
                              cursor: inCatalog ? 'pointer' : 'not-allowed',
                              opacity: inCatalog ? 1 : 0.45,
                            }}
                            title={inCatalog
                              ? 'Choose which organizations see this course'
                              : 'Hidden from the catalog entirely — audience has no effect'}
                            onClick={() => inCatalog && openAudienceDialog(r)}
                          >
                            {audienceSummary(r)}
                          </Tag>
                        </AudienceCell>
                      );
                    },
                  },
                  {
                    header: 'Store',
                    width: 130,
                    render: (r) => renderCheckbox(r, 'storeVisible'),
                  },
                ]}
                emptyText="No courses match your filters."
              />
            </>
          )}
        </Inner>
      </Shell>

      <CourseAudienceDialog
        open={Boolean(audienceCourse)}
        course={audienceCourse}
        organizations={organizations}
        orgsLoading={orgsLoading}
        orgsError={orgsError}
        saving={audienceSaving}
        onClose={() => setAudienceCourse(null)}
        onSave={handleAudienceSave}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
}

const Shell = styled.div`
  padding: 24px 28px 60px;
`;

const Inner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const PageIntro = styled.div`
  margin-bottom: 20px;
  h2 { font-family: ${({ theme }) => theme.fontHead}; font-size: 22px; font-weight: 760; margin: 0 0 4px; letter-spacing: -.01em; }
  p { margin: 0; color: ${({ theme }) => theme.textMuted}; font-size: 13.5px; max-width: 640px; }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.textMuted};
  b { color: ${({ theme }) => theme.text}; }
`;

const LoadingWrap = styled.div`
  display: grid;
  place-items: center;
  padding: 48px 0;
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 220px;
`;

const OwnerWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: ${({ theme }) => theme.fsSm};
    font-weight: 650;
    color: ${({ theme }) => theme.textMuted};
    white-space: nowrap;
  }

  select {
    min-width: 200px;
  }
`;

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AudienceCell = styled.div`
  display: flex;
  align-items: center;
`;

const FieldStatus = styled.span`
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  ${({ $tone, theme }) => ($tone === 'error'
    ? css`color: ${theme.danger};`
    : css`color: ${theme.accentStrong};`)}
`;

const AccessDenied = styled.div`
  max-width: 480px;
  margin: 64px auto;
  padding: 28px 26px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface};

  h2 {
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 19px;
    font-weight: 760;
    margin: 0 0 8px;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.textMuted};
    font-size: 13.5px;
    line-height: 1.5;
  }
`;
